import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setActiveOrgId, getActiveOrgId } from "./api";
import { track } from "./analytics";
import * as log from "./logger";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null while booting
  const [memberships, setMemberships] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [currentOrg, setCurrentOrg] = useState(null);
  const [booted, setBooted] = useState(false);
  const [accessToken, setAccessToken] = useState(null);

  const bootstrap = useCallback(async () => {
    try {
      const [meRes, orgsRes] = await Promise.all([
        api.get("/auth/me"),
        api.get("/orgs"),
      ]);
      const orgListRaw = orgsRes.data || [];
      // Defensive dedupe by organization ID — protects against duplicate
      // membership rows or backend-side dupes that would otherwise render
      // the same workspace twice in the sidebar.
      const seenOrg = new Set();
      const orgList = [];
      for (const entry of orgListRaw) {
        const oid = entry?.organization?.id;
        if (!oid || seenOrg.has(oid)) continue;
        seenOrg.add(oid);
        orgList.push(entry);
      }
      const savedOrg = getActiveOrgId();
      // Default-org preference (closed-beta friendly):
      //   1. Explicit saved org from localStorage.
      //   2. Any org the user is a member of that's marked as an internal
      //      TOVRA platform workspace (Sinovar today).
      //   3. First returned org.
      const initialOrg =
        (savedOrg && orgList.find((o) => o.organization?.id === savedOrg)) ||
        orgList.find((o) => o.organization?.internal_workspace) ||
        orgList[0];
      // set atomically before flipping booted
      setUser(meRes.data.user);
      setMemberships(meRes.data.memberships || []);
      setOrgs(orgList);
      if (initialOrg?.organization) {
        setActiveOrgId(initialOrg.organization.id);
        setCurrentOrg(initialOrg.organization);
      } else {
        setCurrentOrg(null);
      }
    } catch (err) {
      log.debug("[auth] bootstrap failed", err);
      setUser(false);
      setMemberships([]);
      setOrgs([]);
      setCurrentOrg(null);
    } finally {
      setBooted(true);
    }
  }, []);

  useEffect(() => { bootstrap(); }, [bootstrap]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setAccessToken(data.access_token);
    setBooted(false);
    await bootstrap();
    // Fire-and-forget analytics for closed-beta activation metrics.
    track("user_logged_in").catch(() => {});
    return data;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setAccessToken(data.access_token);
    setBooted(false);
    await bootstrap();
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (err) { log.debug("[auth] logout error", err); }
    setUser(false);
    setMemberships([]);
    setOrgs([]);
    setCurrentOrg(null);
    setActiveOrgId(null);
    setBooted(true);
  };

  const switchOrg = (orgId) => {
    const entry = orgs.find((o) => o.organization?.id === orgId);
    if (entry) {
      setActiveOrgId(orgId);
      setCurrentOrg(entry.organization);
    }
  };

  const refreshMe = bootstrap;

  return (
    <AuthCtx.Provider
      value={{
        user, memberships, orgs, currentOrg, accessToken, booted,
        login, register, logout, switchOrg, refreshMe, refresh: refreshMe, setUser,
      }}
    >
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  return useContext(AuthCtx);
}
